/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, DashboardDataModel, TierModel, WidgetModel } from '../../../../models';
import { AfterViewInit, Component } from '@angular/core';
import { DashboardService } from '../../../../services/component';
import { PortalRbacService } from '../../../../services/utils';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-adcs-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})
export class AdcsWidgetComponent extends WidgetComponent implements AfterViewInit {
    constructor(
        dashboardService: DashboardService,
        utilsService: UtilsService,
        private portalRbacService: PortalRbacService
    ) {
        super(dashboardService, utilsService);

        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpPage(data);
    }

    ngAfterViewInit(): void {
        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.setUpPage(data);
                }
            );
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: any): void {
        this.view = null;
        const adcDetails: any = this.formatAdcData(data.adcs);
        this.view = this.getViewData(adcDetails);
        this.loaded = true;
    }

    /**
     * Format ADC data.
     * @param adcs
     * @private
     */
    private formatAdcData(adcs: AdcModel[] = []): any {
        if (adcs?.length) {
            return adcs.reduce((counts, item: AdcModel) => {
                    if (item.adoptionStatus === 'adopted' && item.status === 'online') {
                        counts.active++;
                        if (item.status === 'online') {
                            counts.online++;
                        }
                    } else if (item.adoptionStatus === 'adopted' && item.status === 'offline') {
                        counts.offline++;
                    } else if (item.adoptionStatus !== 'adopted' && item.status === 'offline') {
                        counts.generic++;
                    } else if (item.adoptionStatus === 'pending' || item.status === 'pending') {
                        counts.pending++;
                    }

                    return counts;
                }, { active: 0, online: 0, pending: 0, generic: 0, offline: 0, total: adcs.length }
            );
        }

        return { active: 0, online: 0, pending: 0, generic: 0, offline: 0, total: 0 };
    }

    /**
     * Get view data
     * @param adcDetails
     * @private
     */
    private getViewData(adcDetails: any): WidgetModel {
        const selectedTier: TierModel = this.portalRbacService.selectedTier;
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView);

        const error: any = this.errors?.adcs;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (adcDetails.active || adcDetails.offline) {
            viewObject = this.getActiveMessaging(viewObject, adcDetails);
        }

        if (selectedTier?.adcs) {
            if (this.percentageOfUsage(adcDetails?.total, selectedTier.adcs)) {
                viewObject = this.getNearLimitMessaging(viewObject, adcDetails, selectedTier);
            }

            if (adcDetails.total >= selectedTier.adcs) {
                viewObject = this.getMaxedOutMessaging(viewObject, adcDetails);
            }
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param baseView
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/external/icons/hyper-visors.svg',
            title: 'adcs',
            flagState: 'success',
            subtitle: 'Add an ADC',
            message: 'You have not connected any ADCs yet.',
            linkText: 'Connect an ADC',
            linkUrl: '/cm/adcs/overview',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param adcDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, adcDetails: any): WidgetModel {
        const adcText: 'ADCs' | 'ADC' = adcDetails.total === 1 ? 'ADC' : 'ADCs';
        const newData = {
            flagState: 'hidden',
            subtitle: null,
            message: `You currently have a total of ${adcDetails.total} ${adcText}`,
            linkText: 'View my ADCs',
            linkUrl: '/cm/adcs/list',
            counters: [
                {
                    count: `${adcDetails.online}`,
                    countSubTitle: 'Online',
                    subTitleClass: 'success'
                },
                {
                    count: `${adcDetails.offline}`,
                    countSubTitle: 'Offline',
                    subTitleClass: 'tertiary'
                },
                {
                    count: `${adcDetails.pending}`,
                    countSubTitle: 'Pending',
                    subTitleClass: 'lighter-grey small'
                },
                {
                    count: `${adcDetails.generic}`,
                    countSubTitle: 'Generic',
                    subTitleClass: 'lighter-grey small'
                }
            ]
        };

        return this.updateObject(viewObject, newData);
    }

    /**
     * Get near limit messaging.
     * @param viewObject
     * @param adcDetails
     * @param tier
     * @private
     */
    private getNearLimitMessaging(
        viewObject: WidgetModel,
        adcDetails: any,
        tier: TierModel
    ): WidgetModel {
        const newData = {
            flagState: 'warn',
            // eslint-disable-next-line max-len
            message: `Your current plan allows up to <strong>${tier.adcs}</strong> ADCs, you have <strong>${adcDetails.total}</strong> ADCs in total. See `,
            messageLink: '/cm/organisation/subscriptions',
            messageLinkText: 'Upgrade options',
            counters: [
                {
                    count: `${adcDetails.online}`,
                    countSubTitle: 'Online',
                    subTitleClass: 'success'
                },
                {
                    count: `${adcDetails.offline}`,
                    countSubTitle: 'Offline',
                    subTitleClass: 'tertiary'
                },
                {
                    count: `${adcDetails.pending}`,
                    countSubTitle: 'Pending',
                    subTitleClass: 'lighter-grey small'
                },
                {
                    count: `${adcDetails.generic}`,
                    countSubTitle: 'Generic',
                    subTitleClass: 'lighter-grey small'
                }
            ],
            hasSlider: true,
            sliderContent: {
                message: 'You have almost connected all available ADCs, check out your upgrade options.'
            }
        };

        return this.updateObject(viewObject, newData);
    }

    /**
     * Get maxed out messaging.
     * @param viewObject
     * @param adcDetails
     * @private
     */
    private getMaxedOutMessaging(viewObject: WidgetModel, adcDetails: any): WidgetModel {
        const newData = {
            flagState: 'error',
            message: 'You have used <strong>all</strong> of your ADC connections.<br>See ',
            messageLink: '/cm/organisation/subscriptions',
            messageLinkText: 'Upgrade options',
            counters: [

                {
                    count: `${adcDetails.online}`,
                    countSubTitle: 'Online',
                    subTitleClass: 'success'
                },
                {
                    count: `${adcDetails.offline}`,
                    countSubTitle: 'Offline',
                    subTitleClass: 'tertiary'
                },
                {
                    count: `${adcDetails.pending}`,
                    countSubTitle: 'Pending',
                    subTitleClass: 'lighter-grey small'
                },
                {
                    count: `${adcDetails.generic}`,
                    countSubTitle: 'Generic',
                    subTitleClass: 'lighter-grey small'
                }
            ],
            hasSlider: true,
            sliderContent: {
                message: 'You have maxed out on your ADC connections, check out your upgrade options.'
            }
        };

        return this.updateObject(viewObject, newData);
    }
}
